export interface AudioAnalyser {
  ctx: AudioContext;
  analyser: AnalyserNode;
  gain: GainNode;
  resume: () => Promise<void>;
  readBins: (out: Uint8Array<ArrayBuffer>) => void;
  setVolume: (v: number) => void;
  _source: MediaElementAudioSourceNode;  // exposed for testing
}

export interface CreateAudioAnalyserOptions {
  fftSize?: number;           // power of 2; defaults to 512
  smoothing?: number;          // 0..1; defaults to 0.85
}

/**
 * Wires the given HTMLAudioElement through a Web Audio AnalyserNode.
 * The audio element should already have its src set; this function
 * attaches the audio graph and returns handles for the render loop.
 */
export function createAudioAnalyser(
  audio: HTMLAudioElement,
  opts: CreateAudioAnalyserOptions = {},
): AudioAnalyser {
  const ctx = new AudioContext();
  const analyser = ctx.createAnalyser();
  analyser.fftSize = opts.fftSize ?? 512;
  analyser.smoothingTimeConstant = opts.smoothing ?? 0.85;
  const source = ctx.createMediaElementSource(audio);
  const gain = ctx.createGain();
  gain.gain.value = 0;  // start silent — <audio> can't have `muted` attribute
                        // (would silence the source before AnalyserNode sees it)
  source.connect(analyser);
  analyser.connect(gain);
  gain.connect(ctx.destination);

  return {
    ctx,
    analyser,
    gain,
    _source: source,
    async resume() {
      if (ctx.state === 'suspended') await ctx.resume();
    },
    readBins(out: Uint8Array<ArrayBuffer>) {
      analyser.getByteFrequencyData(out);
    },
    setVolume(v: number) {
      gain.gain.value = Math.max(0, Math.min(1, v));
    },
  };
}
